/**
 * <blog-filters> — client-side filtering + pagination for the main blog listing.
 *
 * Works in two modes:
 *  1. Fallback: filters/paginates the server-rendered article cards already in
 *     the DOM (capped at Liquid's 50-item limit).
 *  2. Storefront API: if a token is provided, fetches EVERY article for the blog
 *     (cursor pagination, no 50 cap), rebuilds the grid + filter options, then
 *     filters/paginates the full set.
 *
 * Element attributes:
 *   data-articles           CSS selector for the article grid container.
 *   data-empty              CSS selector for the "no results" element.
 *   data-pagination         CSS selector for the pagination container.
 *   data-per-page           Posts per page.
 *   data-blog-handle        Blog handle (for the Storefront query).
 *   data-blog-url           Blog base URL (to build article links).
 *   data-show-date          "true" to render the date.
 *   data-storefront-token   Storefront API access token (enables full fetch).
 *   data-storefront-version Storefront API version, e.g. "2025-10".
 */
const CARET_SVG =
  '<svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">' +
  '<path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"/>' +
  '</svg>';

/**
 * URL query-parameter names, following Shopify's Search & Discovery convention.
 * The topic filter is backed by the `custom.filter_tags` metafield, so it uses
 * the native metafield-filter format (`filter.p.m.<namespace>.<key>`).
 * Keys are centralized here so they're easy to rename.
 */
const FILTER_PARAM = {
  topic: 'tag',
  year: 'year',
  month: 'month',
};
const PAGE_PARAM = 'page';

/**
 * Turn a tag value into a clean URL slug: strips diacritics (ä/å→a, ö→o, é→e),
 * lowercases, and replaces spaces with hyphens. Used for both writing and
 * matching the `tag` param, so it round-trips without encoded Swedish letters.
 */
function clampInt(value, min, max, fallback) {
  const parsed = parseInt(value, 10);
  if (Number.isNaN(parsed)) return fallback;
  return Math.min(Math.max(parsed, min), max);
}

function slugifyTag(value) {
  return String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

class BlogFilters extends HTMLElement {
  connectedCallback() {
    this.selects = Array.from(this.querySelectorAll('select[data-filter]'));

    this.grid = document.querySelector(this.getAttribute('data-articles'));
    this.items = this.grid ? Array.from(this.grid.querySelectorAll('.blog-articles__article')) : [];
    this.emptyMessage = document.querySelector(this.getAttribute('data-empty'));
    this.paginationContainer = document.querySelector(this.getAttribute('data-pagination'));

    this.perPage = parseInt(this.getAttribute('data-per-page'), 10) || 6;
    this.currentPage = 1;

    this.blogHandle = this.getAttribute('data-blog-handle');
    this.blogUrl = (this.getAttribute('data-blog-url') || '').replace(/\/$/, '');
    this.showDate = this.getAttribute('data-show-date') === 'true';
    this.token = this.getAttribute('data-storefront-token');
    this.apiVersion = this.getAttribute('data-storefront-version') || '2025-10';
    this.pageSize = clampInt(this.getAttribute('data-storefront-page-size'), 1, 250, 250);
    this.locale = document.documentElement.lang || 'sv-SE';

    this.selects.forEach((select) => {
      select.addEventListener('change', () => {
        this.currentPage = 1;
        this.render();
        this.updateUrl();
      });
    });

    // Re-sync when the user navigates back/forward through filter states.
    window.addEventListener('popstate', () => {
      this.applyStateFromUrl();
      this.render();
    });

    // Restore any filter/page state from the URL, then paint the
    // server-rendered cards.
    this.applyStateFromUrl();
    this.dataset.source = 'rendered';
    this.render();

    // Upgrade to the full set via the Storefront API when a token is present.
    if (this.token && this.blogHandle) {
      this.loadAllArticles();
    }
  }

  /* ------------------------------------------------------------------ *
   * URL state (Search & Discovery style query params)
   * ------------------------------------------------------------------ */

  // Set the selects + current page from the current URL. Only applies a filter
  // value when a matching <option> exists (otherwise it clears that filter).
  applyStateFromUrl() {
    const params = new URLSearchParams(window.location.search);

    this.selects.forEach((select) => {
      const filter = select.dataset.filter;
      const key = FILTER_PARAM[filter];
      const value = key ? params.get(key) : null;

      // Topics match by slug (so ?tag=tjanster resolves to "Tjänster");
      // year/month match case-insensitively.
      const normalize = filter === 'topic' ? slugifyTag : (v) => String(v).toLowerCase();
      const match =
        value !== null
          ? Array.from(select.options).find(
              (option) => option.value && normalize(option.value) === normalize(value)
            )
          : null;
      select.value = match ? match.value : '';
    });

    const page = parseInt(params.get(PAGE_PARAM), 10);
    this.currentPage = Number.isFinite(page) && page > 0 ? page : 1;
  }

  // Reflect the active filters + current page back into the URL.
  updateUrl(replace = false) {
    const params = new URLSearchParams(window.location.search);

    // Clear the params we manage, then re-add the active ones.
    Object.values(FILTER_PARAM).forEach((key) => params.delete(key));
    params.delete(PAGE_PARAM);

    const active = this.getActiveFilters();
    Object.keys(active).forEach((filter) => {
      const key = FILTER_PARAM[filter];
      if (!key || !active[filter]) return;
      // Slugify tag values for clean URLs (?tag=tjanster); read matches by slug too.
      const value = filter === 'topic' ? slugifyTag(active[filter]) : active[filter];
      params.set(key, value);
    });
    if (this.currentPage > 1) params.set(PAGE_PARAM, String(this.currentPage));

    const query = params.toString();
    const url = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    window.history[replace ? 'replaceState' : 'pushState']({}, '', url);
  }

  /* ------------------------------------------------------------------ *
   * Filtering + pagination
   * ------------------------------------------------------------------ */
  getActiveFilters() {
    const active = {};
    this.selects.forEach((select) => {
      active[select.dataset.filter] = select.value;
    });
    return active;
  }

  getMatchingItems() {
    const active = this.getActiveFilters();
    return this.items.filter((item) => {
      const topics = (item.dataset.topics || '').split('||').filter(Boolean);
      const matchesTopic = !active.topic || topics.includes(active.topic);
      const matchesYear = !active.year || item.dataset.year === active.year;
      const matchesMonth = !active.month || item.dataset.month === active.month;
      return matchesTopic && matchesYear && matchesMonth;
    });
  }

  render() {
    const matching = this.getMatchingItems();
    const totalPages = Math.max(1, Math.ceil(matching.length / this.perPage));
    if (this.currentPage > totalPages) this.currentPage = totalPages;

    const start = (this.currentPage - 1) * this.perPage;
    const end = start + this.perPage;

    this.items.forEach((item) => {
      item.hidden = true;
    });
    matching.slice(start, end).forEach((item) => {
      item.hidden = false;
    });

    if (this.emptyMessage) {
      this.emptyMessage.hidden = matching.length !== 0;
    }

    this.renderPagination(totalPages);
  }

  renderPagination(totalPages) {
    if (!this.paginationContainer) return;

    if (totalPages <= 1) {
      this.paginationContainer.innerHTML = '';
      return;
    }

    const list = document.createElement('ul');
    list.className = 'pagination__list list-unstyled';

    if (this.currentPage > 1) {
      list.appendChild(this.createArrow('previous', this.currentPage - 1));
    }
    for (let page = 1; page <= totalPages; page += 1) {
      list.appendChild(this.createPage(page));
    }
    if (this.currentPage < totalPages) {
      list.appendChild(this.createArrow('next', this.currentPage + 1));
    }

    const nav = document.createElement('nav');
    nav.className = 'pagination';
    nav.setAttribute('role', 'navigation');
    nav.setAttribute('aria-label', 'Pagination');
    nav.appendChild(list);

    this.paginationContainer.innerHTML = '';
    this.paginationContainer.appendChild(nav);
  }

  createPage(page) {
    const li = document.createElement('li');
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = page;
    button.setAttribute('aria-label', `Page ${page}`);

    if (page === this.currentPage) {
      button.className = 'pagination__item pagination__item--current';
      button.setAttribute('aria-current', 'page');
    } else {
      button.className = 'pagination__item link';
      button.addEventListener('click', () => this.goToPage(page));
    }

    li.appendChild(button);
    return li;
  }

  createArrow(direction, page) {
    const li = document.createElement('li');
    const button = document.createElement('button');
    button.type = 'button';
    const modifier = direction === 'previous' ? 'pagination__item--next' : 'pagination__item--prev';
    button.className = `pagination__item ${modifier} pagination__item-arrow link motion-reduce`;
    button.setAttribute('aria-label', direction === 'previous' ? 'Previous page' : 'Next page');
    button.innerHTML = CARET_SVG;
    button.addEventListener('click', () => this.goToPage(page));
    li.appendChild(button);
    return li;
  }

  goToPage(page) {
    this.currentPage = page;
    this.render();
    this.updateUrl();
    if (this.grid) {
      const top = this.grid.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }

  /* ------------------------------------------------------------------ *
   * Storefront API
   * ------------------------------------------------------------------ */
  async loadAllArticles() {
    try {
      const articles = await this.fetchAllArticles();
      if (!articles.length) return;
      this.rebuildFromArticles(articles);
      this.dataset.source = 'storefront';
    } catch (error) {
      // Keep the server-rendered fallback on any failure.
      console.warn('[blog-filters] Storefront API fetch failed, using rendered posts.', error);
    }
  }

  async fetchAllArticles() {
    const query = `
      query BlogArticles($handle: String!, $first: Int!, $after: String) {
        blog(handle: $handle) {
          articles(first: $first, after: $after, sortKey: PUBLISHED_AT, reverse: true) {
            pageInfo { hasNextPage endCursor }
            nodes {
              title
              handle
              publishedAt
              tags
              image { url altText width height }
              metafield(namespace: "custom", key: "filter_tags") {
                references(first: 25) {
                  nodes {
                    ... on Metaobject {
                      field(key: "filter_option") { value }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const endpoint = `/api/${this.apiVersion}/graphql.json`;
    const all = [];
    let after = null;
    let hasNext = true;

    while (hasNext) {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.token,
        },
        body: JSON.stringify({ query, variables: { handle: this.blogHandle, first: this.pageSize, after } }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status} from ${endpoint}`);
      }

      const json = await response.json();

      if (json.errors && json.errors.length) {
        throw new Error(`GraphQL errors: ${JSON.stringify(json.errors)}`);
      }

      if (!json.data || !json.data.blog) {
        throw new Error(`No blog returned for handle "${this.blogHandle}" (check blog handle / API version).`);
      }

      const connection = json.data.blog.articles;
      if (!connection) break;

      connection.nodes.forEach((node) => all.push(this.normalizeArticle(node)));
      hasNext = connection.pageInfo.hasNextPage;
      after = connection.pageInfo.endCursor;
    }

    return all;
  }

  normalizeArticle(node) {
    const topics = ((node.metafield && node.metafield.references && node.metafield.references.nodes) || [])
      .map((ref) => ref && ref.field && ref.field.value)
      .filter(Boolean);

    return {
      title: node.title,
      url: `${this.blogUrl}/${node.handle}`,
      publishedAt: node.publishedAt,
      tags: node.tags || [],
      image: node.image || null,
      topics,
    };
  }

  /* ------------------------------------------------------------------ *
   * Rebuild the grid + filter options from fetched data
   * ------------------------------------------------------------------ */
  rebuildFromArticles(articles) {
    if (!this.grid) return;

    this.grid.innerHTML = '';
    this.items = articles.map((article) => this.buildItem(article));
    this.items.forEach((item) => this.grid.appendChild(item));

    this.rebuildTopicOptions(articles);
    this.rebuildYearOptions(articles);

    // Re-apply URL state now that the full option lists exist (e.g. a topic that
    // only appeared beyond the first 50 server-rendered posts), then render.
    this.applyStateFromUrl();
    this.render();
  }

  buildItem(article) {
    const date = article.publishedAt ? new Date(article.publishedAt) : null;

    const wrapper = document.createElement('div');
    wrapper.className = 'blog-articles__article';
    wrapper.dataset.topics = article.topics.join('||');
    if (date) {
      wrapper.dataset.year = String(date.getFullYear());
      wrapper.dataset.month = String(date.getMonth() + 1).padStart(2, '0');
    }

    const card = document.createElement('a');
    card.className = 'article-highlight__card';
    card.href = article.url;

    // Media
    const media = document.createElement('div');
    media.className = 'article-highlight__media';
    if (article.image && article.image.url) {
      const img = document.createElement('img');
      img.src = this.imageUrl(article.image.url, 660);
      img.srcset = [330, 660, 990, 1320]
        .map((w) => `${this.imageUrl(article.image.url, w)} ${w}w`)
        .join(', ');
      img.sizes = '(min-width: 990px) 33vw, (min-width: 750px) 50vw, 100vw';
      img.alt = article.image.altText || article.title;
      img.loading = 'lazy';
      if (article.image.width) img.width = article.image.width;
      if (article.image.height) img.height = article.image.height;
      media.appendChild(img);
    }
    card.appendChild(media);

    // Content
    const content = document.createElement('div');
    content.className = 'article-highlight__content';

    if (article.tags.length) {
      const eyebrow = document.createElement('span');
      eyebrow.className = 'article-highlight__eyebrow';
      eyebrow.textContent = article.tags[0];
      content.appendChild(eyebrow);
    }

    const title = document.createElement('h3');
    title.className = 'article-highlight__card-title';
    title.textContent = article.title;
    content.appendChild(title);

    if (this.showDate && date) {
      const dateEl = document.createElement('span');
      dateEl.className = 'article-highlight__date';
      dateEl.textContent = new Intl.DateTimeFormat(this.locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(date);
      content.appendChild(dateEl);
    }

    card.appendChild(content);
    wrapper.appendChild(card);
    return wrapper;
  }

  imageUrl(url, width) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}width=${width}`;
  }

  rebuildTopicOptions(articles) {
    const select = this.selects.find((s) => s.dataset.filter === 'topic');
    if (!select) return;

    const topics = Array.from(
      new Set(articles.flatMap((article) => article.topics))
    ).sort((a, b) => a.localeCompare(b, this.locale));

    this.replaceOptions(select, topics.map((topic) => ({ value: topic, label: topic })));
  }

  rebuildYearOptions(articles) {
    const select = this.selects.find((s) => s.dataset.filter === 'year');
    if (!select) return;

    const years = Array.from(
      new Set(
        articles
          .filter((article) => article.publishedAt)
          .map((article) => String(new Date(article.publishedAt).getFullYear()))
      )
    ).sort((a, b) => Number(b) - Number(a));

    this.replaceOptions(select, years.map((year) => ({ value: year, label: year })));
  }

  // Keeps the first ("All …") option, replaces the rest, preserves selection.
  replaceOptions(select, options) {
    const previousValue = select.value;
    const allOption = select.options[0];

    select.innerHTML = '';
    if (allOption) select.appendChild(allOption);

    options.forEach(({ value, label }) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      select.appendChild(option);
    });

    if (Array.from(select.options).some((o) => o.value === previousValue)) {
      select.value = previousValue;
    }
  }
}

if (!customElements.get('blog-filters')) {
  customElements.define('blog-filters', BlogFilters);
}
